import React from 'react';
import { FadeIn } from './ui/FadeIn';
import { ActivityIcon, BrainIcon, FingerprintIcon, UsersIcon, CompassIcon, ArrowRightIcon, RotateCcwIcon } from 'lucide-react';
const CHAIN = ['Pensamiento', 'Emoción', 'Estado', 'Decisión', 'Acción', 'Resultado'];
const PILLARS = [{
  name: 'Cuerpo',
  icon: ActivityIcon,
  desc: 'Regulación física: comida, movimiento, sueño e hidratación. Cuando le das al cuerpo señales de seguridad, la mente baja el volumen.'
}, {
  name: 'Mente',
  icon: BrainIcon,
  desc: 'Claridad mental: vaciás el ruido, observás tus pensamientos y dejás de confundir una emoción con un hecho.'
}, {
  name: 'Identidad',
  icon: FingerprintIcon,
  desc: 'Quién sos de verdad, no el personaje que sostenés para afuera. Acá se cae la máscara y aparece la certeza.'
}, {
  name: 'Familia',
  icon: UsersIcon,
  desc: 'El impacto real. Cuando volvés a vos, tu casa lo siente. Presencia en lugar de restos de energía.'
}, {
  name: 'Propósito',
  icon: CompassIcon,
  desc: 'Decisiones tomadas desde dirección, no desde miedo. Lo que hacés empieza a tener sentido con quien sos.'
}];
const PHASES = [{
  weeks: 'Semanas 1–4',
  title: 'VER',
  desc: 'Identificás tu loop específico. No el loop en general: el tuyo, con nombre y apellido.'
}, {
  weeks: 'Semanas 5–8',
  title: 'ELEGIR',
  desc: 'Te enseño a ser observador de tu pensamiento. Cuando aprendés a notarlo antes de reaccionar, podés reconocer: "esto no soy yo, es una versión vieja mía reaccionando". Ahí, en ese segundo, elegís un pensamiento nuevo. Un pensamiento nuevo genera una emoción nueva. Una emoción nueva genera una acción nueva. Una acción nueva genera un resultado nuevo.'
}, {
  weeks: 'Semanas 9–12',
  title: 'CONSTRUIR',
  desc: 'Operás desde certeza, no desde miedo. La coherencia pasa a ser tu punto de partida.'
}];
export function MechanismSection() {
  return <section id="mechanism" className="py-14 md:py-32 bg-charcoal-dark border-y border-charcoal-light" data-id="element-232" data-mp="1cwyyy9i65n63">
      <div className="max-w-6xl mx-auto px-6" data-id="element-233" data-mp="17xw2vek62ra9">
        <FadeIn className="text-center mb-10 md:mb-16 max-w-3xl mx-auto" data-id="element-234" data-mp="1808j9naa0tl7">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-offwhite mb-6" data-id="element-235" data-mp="12zud6as8jtpt">
            El Mecanismo — Por qué ELEVATE funciona
          </h2>
          <p className="text-lg text-offwhite-dim" data-id="element-236" data-mp="1vngo643sm0mp">
            Tu realidad no es un accidente. Se construye en cadena:
          </p>

          <div className="mt-8 flex flex-wrap justify-center items-center gap-x-3 gap-y-2 text-sm md:text-base font-medium text-sand" data-id="element-237" data-mp="1qpyda867tbc2">
            {CHAIN.map((step, i) => <React.Fragment key={step} data-id="element-238" data-mp="1hx4uw1vh1vtz" data-mpi={i}>
                <span className={i === CHAIN.length - 1 ? 'text-amber font-bold' : undefined} data-id="element-239" data-mp="1hw385053fkwf">
                  {step}
                </span>
                {i < CHAIN.length - 1 && <ArrowRightIcon className="w-4 h-4 text-charcoal-light" data-id="element-240" data-mp="3sb0z671nki2b" />}
              </React.Fragment>)}
            <span className="flex items-center gap-2 text-offwhite-dim/60 text-xs uppercase tracking-wider" data-id="element-241" data-mp="1481ve9zd332">
              <RotateCcwIcon className="w-4 h-4" data-id="element-242" data-mp="57nyiam1v3fr" />
              vuelve al inicio
            </span>
          </div>

          <p className="mt-8 text-offwhite-dim leading-relaxed" data-id="element-243" data-mp="f3qwd49vcfww">
            No empezamos por la mente. Empezamos por el cuerpo — porque fue ahí
            donde yo empecé a confiar en mí de nuevo. El entrenamiento regula la
            emoción antes de que la mente pueda cambiar un solo pensamiento.
            Desde esa base recién se puede trabajar pensamientos, autosabotajes,
            creencias limitantes y autoestima — hasta que ese trabajo empieza a
            notarse también en la familia.
          </p>
          <p className="mt-4 text-xl font-heading font-bold text-offwhite" data-id="element-244" data-mp="12ssc7cl16tc7">
            Ese orden no es teoría: es el camino que yo recorrí, ahora guiado
            para que no te lleve 10 años.
          </p>
        </FadeIn>

        {/* 5 PILLARS */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-id="element-245" data-mp="k5fcb7pngat0">
          {PILLARS.map((pillar, i) => {
          const Icon = pillar.icon;
          const isLead = i === 0;
          return <FadeIn key={pillar.name} delay={i * 0.08} direction="up" className={`bg-charcoal border border-charcoal-light p-6 md:p-8 rounded-sm flex flex-col ${isLead ? 'lg:col-span-1' : ''}`} data-id="element-246" data-mp="67qt04b8dbq2" data-mpi={i}>
                <div className="flex items-center gap-4 mb-4" data-id="element-247" data-mp="b6aey4ptqnif">
                  <div className="bg-sand/10 p-3 rounded-sm shrink-0" data-id="element-248" data-mp="b8wm438sz104">
                    <Icon className="w-6 h-6 text-sand" data-id="element-249" data-mp="1rjqw5wr73kql" />
                  </div>
                  <h3 className="text-lg font-heading font-bold text-offwhite uppercase tracking-wider" data-id="element-250" data-mp="1mkxdzr0yr5k3">
                    {pillar.name}
                  </h3>
                </div>
                <p className="text-offwhite-dim text-sm md:text-base leading-relaxed" data-id="element-251" data-mp="1z5455vqkhwa4">
                  {pillar.desc}
                </p>
              </FadeIn>;
        })}

          <FadeIn delay={0.4} direction="up" className="bg-sand/10 border border-sand/30 p-6 md:p-8 rounded-sm flex flex-col justify-center" data-id="element-252" data-mp="1p654azc1n4xo">
            <p className="text-lg md:text-xl font-heading font-bold text-sand leading-snug" data-id="element-253" data-mp="2hv2m71ccezpw">
              Los 5 pilares no se trabajan por separado. Se trabajan
              conectados, en progresión.
            </p>
            <p className="mt-3 text-sm text-offwhite-dim" data-id="element-254" data-mp="284r5sfbh2y24">
              Por eso el cambio se sostiene cuando el programa termina.
            </p>
          </FadeIn>
        </div>

        {/* PHASES */}
        <div className="mt-12 md:mt-20" data-id="element-255" data-mp="30m3mbsp791b1">
          <FadeIn data-id="element-256" data-mp="30n4yk0hybtuc">
            <h3 className="text-xl md:text-2xl font-heading font-bold text-offwhite text-center mb-8 md:mb-12" data-id="element-257" data-mp="30nendkr9fuyg">
              Las 12 semanas, en 3 fases
            </h3>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-4 md:gap-6" data-id="element-258" data-mp="36duli2dz4zg2">
            {PHASES.map((phase, i) => <FadeIn key={phase.title} delay={i * 0.12} direction="up" className="relative bg-charcoal border-t-2 border-sand p-6 md:p-8 rounded-sm flex flex-col" data-id="element-259" data-mp="31iyrwxt3a40u" data-mpi={i}>
                <span className="text-xs font-medium text-offwhite-dim/60 uppercase tracking-[0.2em]" data-id="element-260" data-mp="2mrpuw3muxgdo">
                  {phase.weeks}
                </span>
                <h4 className="mt-3 text-2xl font-heading font-bold text-sand tracking-wider" data-id="element-261" data-mp="2io091lxkhcwc">
                  {phase.title}
                </h4>
                <p className="mt-4 text-offwhite-dim leading-relaxed" data-id="element-262" data-mp="28r5c37gxzkk6">
                  {phase.desc}
                </p>
              </FadeIn>)}
          </div>
        </div>

        <FadeIn delay={0.2} className="mt-10 md:mt-16 text-center" data-id="element-263" data-mp="1tpouughk5ovl">
          <p className="text-xl md:text-2xl font-heading font-bold text-sand" data-id="element-264" data-mp="1trjgjx0nll0q">
            12 semanas · 1 sesión por semana · Ejercicios de 10–20 minutos
            diarios
          </p>
          <p className="mt-3 text-lg text-offwhite-dim" data-id="element-265" data-mp="23la2au5ebhcq">
            No perfecto.{' '}
            <span className="text-offwhite font-bold" data-id="element-266" data-mp="28lo783bpab6j">Completo.</span>
          </p>
        </FadeIn>
      </div>
    </section>;
}